/**
 * メモにハイライトを引用するときの選択ダイアログ。複数選んでまとめて挿入できる
 */

import { Check, Search } from 'lucide-react'
import { useEffect, useState } from 'react'

import { type HighlightSource, listHighlightSources } from '@/api/services/highlight'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Spinner } from '@/components/ui/spinner'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { cn } from '@/lib/utils'
import { HIGHLIGHT_COLORS } from '@/pages/reader/highlight/highlight-colors'

import { formatHighlightLocation, formatHighlightSource } from './highlight-ref-cache'

// 読書ノートを開いている間は、その作品のハイライトを初めに出す
let pickerProductId: number | null = null

export const useHighlightPickerProduct = (productId: number | null) => {
  useEffect(() => {
    pickerProductId = productId
    return () => {
      pickerProductId = null
    }
  }, [productId])
}

const RECENT_LIMIT = 100
const PRODUCT_LIMIT = 500

export const highlightColorHex = (color: string): string =>
  HIGHLIGHT_COLORS.find((c) => c.value === color)?.hex ?? HIGHLIGHT_COLORS[0].hex

type Props = {
  onPick: (highlights: HighlightSource[]) => void
  onClose: () => void
}

export function HighlightPickerDialog({ onPick, onClose }: Props) {
  const [productId] = useState(pickerProductId)
  const [onlyThisBook, setOnlyThisBook] = useState(productId !== null)
  const [query, setQuery] = useState('')
  const debouncedQuery = useDebouncedValue(query.trim())
  const [results, setResults] = useState<HighlightSource[] | null>(null)
  const [selected, setSelected] = useState<HighlightSource[]>([])

  useEffect(() => {
    let cancelled = false
    setResults(null)
    const byProduct = onlyThisBook && productId !== null
    listHighlightSources({
      q: debouncedQuery || undefined,
      product_id: byProduct ? productId : undefined,
      limit: byProduct ? PRODUCT_LIMIT : RECENT_LIMIT,
    })
      .then((list) => {
        if (!cancelled) setResults(list)
      })
      .catch((err) => {
        console.warn('[highlight-ref] failed to search highlights', err)
        if (!cancelled) setResults([])
      })
    return () => {
      cancelled = true
    }
  }, [debouncedQuery, onlyThisBook, productId])

  const toggle = (h: HighlightSource) =>
    setSelected((prev) => (prev.some((p) => p.id === h.id) ? prev.filter((p) => p.id !== h.id) : [...prev, h]))

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="flex max-h-[85dvh] flex-col gap-3 p-4 sm:max-w-xl">
        <DialogTitle className="text-base">ハイライトを引用</DialogTitle>
        <DialogDescription className="sr-only">メモに引用するハイライトを選びます</DialogDescription>

        <div className="relative">
          <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="本文・メモ・作品名で検索"
            className="pl-8"
            autoFocus
          />
        </div>

        {productId !== null && (
          <div className="flex gap-1 text-xs">
            {[
              { value: true, label: 'この本' },
              { value: false, label: 'すべての本' },
            ].map((opt) => (
              <button
                key={opt.label}
                type="button"
                aria-pressed={onlyThisBook === opt.value}
                onClick={() => setOnlyThisBook(opt.value)}
                className={cn(
                  'rounded-full border px-3 py-1',
                  onlyThisBook === opt.value ? 'border-primary bg-primary text-primary-foreground' : 'text-muted-foreground',
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}

        <div className="-mx-1 min-h-40 flex-1 overflow-y-auto px-1">
          {results === null ? (
            <div className="flex justify-center py-10">
              <Spinner size={24} />
            </div>
          ) : results.length === 0 ? (
            <p className="py-10 text-center text-sm text-muted-foreground">
              {debouncedQuery ? '一致するハイライトがありません' : 'ハイライトがありません'}
            </p>
          ) : (
            <ul className="space-y-1.5">
              {results.map((h) => {
                const isSelected = selected.some((p) => p.id === h.id)
                const location = formatHighlightLocation(h)
                return (
                  <li key={h.id}>
                    <button
                      type="button"
                      aria-pressed={isSelected}
                      onClick={() => toggle(h)}
                      className={cn(
                        'flex w-full gap-2 rounded-md border p-2! text-left transition-colors',
                        isSelected ? 'border-primary bg-primary/5' : 'hover:bg-accent/50',
                      )}
                    >
                      <span
                        className="w-1 shrink-0 self-stretch rounded-full"
                        style={{ backgroundColor: highlightColorHex(h.color) }}
                      />
                      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                        <span className="line-clamp-3 text-sm">{h.text || '（本文なし）'}</span>
                        {h.note && <span className="line-clamp-2 text-xs text-muted-foreground">✎ {h.note}</span>}
                        <span className="truncate text-[11px] text-muted-foreground">
                          {formatHighlightSource(h)}
                          {location && ` · ${location}`}
                        </span>
                      </span>
                      <span
                        className={cn(
                          'mt-0.5 flex size-4.5 shrink-0 items-center justify-center rounded-full border',
                          isSelected && 'border-primary bg-primary text-primary-foreground',
                        )}
                      >
                        {isSelected && <Check className="size-3" />}
                      </span>
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>
            キャンセル
          </Button>
          <Button size="sm" disabled={selected.length === 0} onClick={() => onPick(selected)}>
            {selected.length > 0 ? `${selected.length} 件を挿入` : '挿入'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
